import React from 'react';
import { ArrowPathIcon, ClockIcon } from '@heroicons/react/24/outline';
import type { EnhancedChatSession } from '../services/webChatHistoryService';

export type SidebarRunState = 'running' | 'new' | null;

/**
 * Der Laufzustand eines Chats in der Seitenleiste (docs/superpowers/specs/2026-10-16-durable-research-runs.md,
 * Oberfläche): Spinner, solange ein Lauf aktiv ist, in diesem Tab oder laut Server (auch von einem anderen Gerät); Punkt
 * „neu“ für den letzten endgültigen, ungesehenen Lauf. Der geöffnete Chat und Läufe, die dieser Tab schon als gesehen
 * gemeldet hat, zeigen keinen Punkt.
 */
export function sidebarRunState(
  session: Pick<EnhancedChatSession, 'activeRun' | 'unseenRun'>,
  view: { searching: boolean; current: boolean; seenRunIds: ReadonlySet<string> },
): SidebarRunState {
  if (view.searching || session.activeRun) return 'running';
  if (session.unseenRun && !view.current && !view.seenRunIds.has(session.unseenRun.id)) return 'new';
  return null;
}

interface SidebarRunIndicatorProps {
  state: SidebarRunState;
  /** Die Uhrzeit der letzten Nachricht, solange kein Lauf aktiv ist. */
  time: string;
}

const SidebarRunIndicator: React.FC<SidebarRunIndicatorProps> = ({ state, time }) => {
  if (state === 'running') {
    return (
      <div className="flex items-center mt-1 text-xs text-accent">
        <ArrowPathIcon className="w-3 h-3 mr-1 animate-spin" />
        <span>Recherche läuft …</span>
      </div>
    );
  }
  return (
    <div className="flex items-center mt-1 text-xs text-muted">
      <ClockIcon className="w-3 h-3 mr-1" />
      {time}
      {state === 'new' && (
        <span className="ml-2 inline-flex items-center gap-1 font-medium text-accent" title="Fertige Recherche, noch nicht geöffnet">
          <span className="h-2 w-2 rounded-full bg-accent" aria-hidden="true" />
          <span>neu</span>
        </span>
      )}
    </div>
  );
};

export default SidebarRunIndicator;
